import {
  and,
  asc,
  bill_of_materials,
  bom_component,
  desc,
  eq,
  inventory_count,
  inventory_count_line,
  isNotNull,
  landed_cost,
  landed_cost_line,
  location,
  lot,
  manufacturing_order,
  type PowerSyncDrizzleDB,
  product,
  product_category,
  product_supplier,
  purchase_order,
  purchase_order_line,
  purchase_return,
  purchase_return_line,
  reorder_rule,
  stock_move,
  stock_move_line,
  stock_picking,
  stock_quant,
  uom,
  valuation_layer,
  vendor,
  warehouse,
} from "@stockpilot/powersync";
import {
  liveLocationFilters,
  liveProductFilters,
  liveStockPickingFilters,
  liveWarehouseFilters,
} from "../live/filters";

/** LIVE products from an organization, sorted by name (trash excluded). */
export function getProductsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(product)
    .where(and(eq(product.organization_id, orgId), ...liveProductFilters()))
    .orderBy(product.name);
}

/** One product by id. */
export function getProductByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(product).where(eq(product.id, id));
}

/** Product categories from an organization. */
export function getCategoriesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(product_category)
    .where(eq(product_category.organization_id, orgId))
    .orderBy(product_category.name);
}

/** Units of measure from an organization. */
export function getUomsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(uom).where(eq(uom.organization_id, orgId)).orderBy(uom.name);
}

/** LIVE warehouses from an organization (trash excluded). */
export function getWarehousesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(warehouse)
    .where(and(eq(warehouse.organization_id, orgId), ...liveWarehouseFilters()))
    .orderBy(warehouse.name);
}

/** LIVE locations from an organization (trash excluded; warehouse/parent cohort). */
export function getLocationsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(location)
    .where(and(eq(location.organization_id, orgId), ...liveLocationFilters()))
    .orderBy(location.name);
}

/** Quants (on-hand per product×location) from an organization. */
export function getStockQuantsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(stock_quant).where(eq(stock_quant.organization_id, orgId));
}

/** On-hand for a product at a specific location. */
export function getQuantQuery(db: PowerSyncDrizzleDB, productId: string, locationId: string) {
  return db
    .select()
    .from(stock_quant)
    .where(and(eq(stock_quant.product_id, productId), eq(stock_quant.location_id, locationId)));
}

/** A specific movement (for recording its counter-movement). */
export function getMoveByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(stock_move).where(eq(stock_move.id, id));
}

/**
 * Map of id→name for ALL visible products (trash INCLUDED and ALL
 * synchronized orgs): to render names in the activity feed even when
 * the product is deleted or belongs to another member's org. The local
 * SQLite contains only accessible rows (streams), so there is no need to
 * re-filter by org.
 */
export function getProductNamesQuery(db: PowerSyncDrizzleDB) {
  return db.select({ id: product.id, name: product.name }).from(product);
}

/** Kardex: movements for a product, most recent first. */
export function getMovesByProductQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db
    .select()
    .from(stock_move)
    .where(eq(stock_move.product_id, productId))
    .orderBy(desc(stock_move.created_at));
}

// ─── OPERATIONS (Stockpilot F2) ────────────────────────────────────────────

/** LIVE operations (pickings) from an organization, most recent first. */
export function getPickingsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(stock_picking)
    .where(and(eq(stock_picking.organization_id, orgId), ...liveStockPickingFilters()))
    .orderBy(desc(stock_picking.created_at));
}

/** One operation by id. */
export function getPickingByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(stock_picking).where(eq(stock_picking.id, id));
}

/** Lines of an operation. */
export function getMoveLinesByPickingQuery(db: PowerSyncDrizzleDB, pickingId: string) {
  return db
    .select()
    .from(stock_move_line)
    .where(eq(stock_move_line.picking_id, pickingId))
    .orderBy(stock_move_line.created_at);
}

// ─── REPLENISHMENT (Stockpilot F2) ───────────────────────────────────────

/** Replenishment rules (min/max) from an organization. */
export function getReorderRulesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(reorder_rule)
    .where(eq(reorder_rule.organization_id, orgId))
    .orderBy(desc(reorder_rule.created_at));
}

// ─── PURCHASES (Stockpilot F3) ────────────────────────────────────────────────

/** Vendors from an organization. */
export function getVendorsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(vendor).where(eq(vendor.organization_id, orgId)).orderBy(vendor.name);
}

/** Vendor-product prices from an organization. */
export function getProductSuppliersQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(product_supplier).where(eq(product_supplier.organization_id, orgId));
}

/** Vendor prices for a specific product. */
export function getSuppliersByProductQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db.select().from(product_supplier).where(eq(product_supplier.product_id, productId));
}

/** Purchase orders from an organization, most recent first. */
export function getPurchaseOrdersQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(purchase_order)
    .where(eq(purchase_order.organization_id, orgId))
    .orderBy(desc(purchase_order.created_at));
}

/** One purchase order by id. */
export function getPurchaseOrderByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(purchase_order).where(eq(purchase_order.id, id));
}

/** Lines of a purchase order. */
export function getPurchaseOrderLinesQuery(db: PowerSyncDrizzleDB, poId: string) {
  return db
    .select()
    .from(purchase_order_line)
    .where(eq(purchase_order_line.po_id, poId))
    .orderBy(asc(purchase_order_line.created_at));
}

// ─── VALUATION (Stockpilot F3) ─────────────────────────────────────────────

/** Valuation layers from an organization, in chronological order (AVCO). */
export function getValuationLayersQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(valuation_layer)
    .where(eq(valuation_layer.organization_id, orgId))
    .orderBy(asc(valuation_layer.created_at));
}

/** Valuation layers for a product, in chronological order. */
export function getValuationLayersByProductQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db
    .select()
    .from(valuation_layer)
    .where(eq(valuation_layer.product_id, productId))
    .orderBy(asc(valuation_layer.created_at));
}

// ─── TRACEABILITY + COUNTS (Stockpilot F4) ─────────────────────────────────

/** Lots / serials from an organization, by expiration (FEFO) and then name. */
export function getLotsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(lot)
    .where(eq(lot.organization_id, orgId))
    .orderBy(asc(lot.expiry_date), asc(lot.name));
}

/** Lots for a specific product, by expiration (FEFO). */
export function getLotsByProductQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db
    .select()
    .from(lot)
    .where(eq(lot.product_id, productId))
    .orderBy(asc(lot.expiry_date), asc(lot.name));
}

/** On-hand per lot for a product at a location (quants with lot_id). */
export function getQuantsByProductLocationQuery(
  db: PowerSyncDrizzleDB,
  productId: string,
  locationId: string,
) {
  return db
    .select()
    .from(stock_quant)
    .where(and(eq(stock_quant.product_id, productId), eq(stock_quant.location_id, locationId)));
}

/** Inventory count documents from an organization, most recent first. */
export function getInventoryCountsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(inventory_count)
    .where(eq(inventory_count.organization_id, orgId))
    .orderBy(desc(inventory_count.created_at));
}

/** One count document by id. */
export function getInventoryCountByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(inventory_count).where(eq(inventory_count.id, id));
}

/** Lines of a count document. */
export function getInventoryCountLinesQuery(db: PowerSyncDrizzleDB, countId: string) {
  return db
    .select()
    .from(inventory_count_line)
    .where(eq(inventory_count_line.count_id, countId))
    .orderBy(asc(inventory_count_line.created_at));
}

// ─── MANUFACTURING + LANDED COSTS + RETURNS (Stockpilot F5) ────────────────

/** Bills of materials (BoM) from an organization. */
export function getBomsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(bill_of_materials)
    .where(eq(bill_of_materials.organization_id, orgId))
    .orderBy(desc(bill_of_materials.created_at));
}

/** BoMs for a specific finished product. */
export function getBomsByProductQuery(db: PowerSyncDrizzleDB, productId: string) {
  return db.select().from(bill_of_materials).where(eq(bill_of_materials.product_id, productId));
}

/** Components of a BoM. */
export function getBomComponentsQuery(db: PowerSyncDrizzleDB, bomId: string) {
  return db
    .select()
    .from(bom_component)
    .where(eq(bom_component.bom_id, bomId))
    .orderBy(asc(bom_component.created_at));
}

/** Manufacturing orders from an organization, most recent first. */
export function getManufacturingOrdersQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(manufacturing_order)
    .where(eq(manufacturing_order.organization_id, orgId))
    .orderBy(desc(manufacturing_order.created_at));
}

/** One manufacturing order by id. */
export function getManufacturingOrderByIdQuery(db: PowerSyncDrizzleDB, id: string) {
  return db.select().from(manufacturing_order).where(eq(manufacturing_order.id, id));
}

/** Landed costs from an organization, most recent first. */
export function getLandedCostsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(landed_cost)
    .where(eq(landed_cost.organization_id, orgId))
    .orderBy(desc(landed_cost.created_at));
}

/** Lines (allocation) of a landed cost. */
export function getLandedCostLinesQuery(db: PowerSyncDrizzleDB, landedCostId: string) {
  return db
    .select()
    .from(landed_cost_line)
    .where(eq(landed_cost_line.landed_cost_id, landedCostId))
    .orderBy(asc(landed_cost_line.created_at));
}

/**
 * Receipts (pickings of type 'receipt') from an organization, for landed costs.
 *
 * COHORT BUG fixed (scattered invariant): this read was RAW (without
 * `deleted_at`), so a receipt sent to the trash still appeared
 * in the landed-cost selector (`apps/web/.../manufacturing.tsx`). It now
 * composes `liveStockPickingFilters()`, just like `getPickingsQuery`.
 */
export function getReceiptsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(stock_picking)
    .where(
      and(
        eq(stock_picking.organization_id, orgId),
        eq(stock_picking.kind, "receipt"),
        ...liveStockPickingFilters(),
      ),
    )
    .orderBy(desc(stock_picking.created_at));
}

/** Vendor returns from an organization, most recent first. */
export function getPurchaseReturnsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(purchase_return)
    .where(eq(purchase_return.organization_id, orgId))
    .orderBy(desc(purchase_return.created_at));
}

/** Lines of a vendor return. */
export function getPurchaseReturnLinesQuery(db: PowerSyncDrizzleDB, returnId: string) {
  return db
    .select()
    .from(purchase_return_line)
    .where(eq(purchase_return_line.return_id, returnId))
    .orderBy(asc(purchase_return_line.created_at));
}

// ─── TRASH (ACTIVITY-CORE L2) ────────────────────────────────────────────
// Views of soft-deleted data (deleted_at IS NOT NULL). Restore = clearing the
// field (mutations restore*). There is no purge: moves are append-only and the
// write path turns any hard DELETE into a soft-delete.

/** Products in the trash, most recent first. */
export function getTrashedProductsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(product)
    .where(and(eq(product.organization_id, orgId), isNotNull(product.deleted_at)))
    .orderBy(desc(product.updated_at));
}

/** Warehouses in the trash. */
export function getTrashedWarehousesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(warehouse)
    .where(and(eq(warehouse.organization_id, orgId), isNotNull(warehouse.deleted_at)))
    .orderBy(desc(warehouse.updated_at));
}

/** Locations in the trash. */
export function getTrashedLocationsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(location)
    .where(and(eq(location.organization_id, orgId), isNotNull(location.deleted_at)))
    .orderBy(desc(location.updated_at));
}

/** Operations in the trash. */
export function getTrashedPickingsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(stock_picking)
    .where(and(eq(stock_picking.organization_id, orgId), isNotNull(stock_picking.deleted_at)))
    .orderBy(desc(stock_picking.updated_at));
}
